import {t} from '@lingui/macro'

import {BillingInterval, Interval} from '@product-web/api-types/billing'
import type {PlanType} from '@product-web/api-types/plan'
import type {CpqRatePlanType} from '@product-web/bff-moons/generated/beyond'
import {getFeatureCountryConfiguration} from '@product-web/country-configuration/configuration'
import {Feature} from '@product-web/country-configuration/types'
import {reportError} from '@product-web/error/report'
import type Country from '@product-web/locale/country'
import type {MinorCurrency} from '@product-web/locale/currency'
import {CurrencyFormat, formatCurrency, formatMinorCurrency} from '@product-web/locale/currency'
import {useToaster} from '@product-web/toaster'

import {bff} from '../bff-hooks'

export const toStringNoDecimal = (amount: number | undefined, currency: string): string =>
    formatCurrency(amount, currency, {format: CurrencyFormat.FixedFractionDigits(0)})

export const isPayingAnnually = (
    commitmentInterval?: Interval,
    billingInterval?: BillingInterval,
    isCashbackCountry?: boolean,
): boolean => {
    if (isCashbackCountry) {
        return billingInterval === BillingInterval.YEARLY
    }

    return commitmentInterval === Interval.YEAR
}

export interface PricePlanPrices {
    monthly: number
    monthlyDiscounted: number
    yearly: number
    yearlyDiscounted: number
}
export interface PricePlanDetails {
    prices: PricePlanPrices
    currency: string | undefined
}

export const usePricingBreakDown = (
    planType: PlanType,
    planInterval: Interval,
    billingInterval: BillingInterval,
    country?: Country,
) => {
    const {isCashBackCountry} = getFeatureCountryConfiguration(Feature.Paywall, country)
    const {showToast} = useToaster()
    const ratePlanType = planType as CpqRatePlanType

    const {data: subscriptionPriceEstimations, isLoading: isSubscriptionPriceEstimationsLoading} =
        bff.paywall.choosePlanModal.getPlanPriceEstimations.useQuery(
            {
                ratePlanType,
            },
            {
                enabled: !!ratePlanType,
                onError(error) {
                    reportError(error)
                    showToast(t`An error occurred. Please try again later or contact support.`, {
                        level: 'error',
                    })
                },
            },
        )

    const yearlyPriceWithoutDiscount = subscriptionPriceEstimations?.YEAR.totalWithoutTax
        .value as MinorCurrency
    const yearlyPriceWithDiscount = subscriptionPriceEstimations?.YEAR.totalWithoutTaxDiscounted
        .value as MinorCurrency
    const monthlyPriceWithoutDiscount = subscriptionPriceEstimations?.MONTH.totalWithoutTax
        .value as MinorCurrency
    const monthlyPriceWithDiscount = subscriptionPriceEstimations?.MONTH.totalWithoutTaxDiscounted
        .value as MinorCurrency
    const currency = subscriptionPriceEstimations?.YEAR.totalWithoutTax.currency ?? ''

    const hasDiscount =
        planInterval === Interval.YEAR &&
        (!isCashBackCountry || (isCashBackCountry && billingInterval === BillingInterval.YEARLY)) &&
        yearlyPriceWithDiscount < yearlyPriceWithoutDiscount
    return {
        isValidating: isSubscriptionPriceEstimationsLoading,
        currency,
        hasDiscount,
        monthlyPrice: {
            standardFormatted: formatMinorCurrency({value: monthlyPriceWithoutDiscount, currency}),
            standard: monthlyPriceWithoutDiscount,
            discountedFormatted: hasDiscount
                ? formatMinorCurrency({value: monthlyPriceWithDiscount, currency})
                : undefined,
            discounted: hasDiscount ? monthlyPriceWithDiscount : undefined,
        },
        yearlyPrice: {
            standardFormatted: formatMinorCurrency({value: yearlyPriceWithoutDiscount, currency}),
            standard: yearlyPriceWithoutDiscount,
            discountedFormatted: hasDiscount
                ? formatMinorCurrency({value: yearlyPriceWithDiscount, currency})
                : undefined,
            discounted: hasDiscount ? yearlyPriceWithDiscount : undefined,
        },
    }
}
